import React, { useState, useEffect, useCallback } from 'react';
import { 
  Grid, 
  Paper, 
  Typography, 
  useTheme, 
  Box, 
  LinearProgress, 
  alpha, 
  Collapse,
  IconButton, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  Fade,
  Badge,
  Stack,
  Divider,
  Avatar,
  CircularProgress,
  Tooltip
} from '@mui/material';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import RoadIcon from '@mui/icons-material/Construction';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LaunchIcon from '@mui/icons-material/Launch';
import { useNavigate } from 'react-router-dom';

// import DetectionDetailModal from './DetectionDetailModal'; // DISABLED

// Import hooks from DefectsPage
import useDefectStats from '../Defects(ENVIS)/hooks/useDefectStats';
import { useAuth } from '../../context/AuthContext';
import { envisFindingsApi } from '../../services/api';

// Import the DefectValidationModal for viewing
import DefectValidationModal from '../Defects(ENVIS)/modals/DefectValidationModal';

// Function to generate defect data from real stats
const getDefectDataFromStats = (stats, loading) => {
  if (loading) {
    return {
      potholes: { id: 'potholes', title: "Unresolved Potholes", count: '...', progress: 0, description: "Loading data...", slogan: "Towards Zero Pothole", Icon: RoadIcon, color: 'error.dark', trend: '-', featured: true },
      total: { id: 'total', title: "Total Detections", count: '...', progress: 0, description: "... total detections, ... pending", Icon: DataUsageIcon, color: 'info.main', trend: '-' },
      within: { id: 'within', title: "Within Response Time", count: '...', progress: 0, description: "Loading data...", Icon: ScheduleIcon, color: 'success.main', trend: '-' },
      temp: { id: 'temp', title: "Temp Fix Overdue", count: '...', progress: 0, description: "Loading data...", Icon: WarningAmberIcon, color: 'warning.main', trend: '-' },
      perm: { id: 'perm', title: "Perm Fix Overdue", count: '...', progress: 0, description: "Loading data...", Icon: ErrorIcon, color: 'error.main', trend: '-' },
      closed: { id: 'closed', title: "Closed Detection", count: '...', progress: 0, description: "Loading data...", Icon: CheckCircleIcon, color: 'grey.600', trend: '-' },
    };
  }

  const total = stats.total || 0;
  const validated = stats.validated || 0;
  const draft = stats.draft || 0;
  const onTime = stats.onTimeDefects || 0;
  const dueSoon = stats.dueSoonDefects || 0;
  const overdue = stats.overdueDefects || 0;
  const resolved = stats.resolved || 0;
  const unresolvedPotholes = stats.unresolvedPotholes || 0;

  // Calculate progress percentages
  const onTimeProgress = total > 0 ? Math.round((onTime / total) * 100) : 0;
  const dueSoonProgress = total > 0 ? Math.round((dueSoon / total) * 100) : 0;
  const overdueProgress = total > 0 ? Math.round((overdue / total) * 100) : 0;
  const resolvedProgress = total > 0 ? Math.round((resolved / total) * 100) : 0;

  return {
    potholes: { 
      id: 'potholes', 
      title: "Unresolved Potholes", 
      count: unresolvedPotholes, 
      progress: total > 0 ? Math.round((unresolvedPotholes / total) * 100) : 0, 
      description: `Critical road safety priority`,
      slogan: "Towards Zero Pothole",
      Icon: RoadIcon, 
      color: 'error.dark', 
      trend: unresolvedPotholes > 0 ? '+' + unresolvedPotholes : '0',
      featured: true
    },
    total: { 
      id: 'total', 
      title: "Validated Detections", 
      count: validated, 
      progress: total > 0 ? Math.round((validated / total) * 100) : 0, 
      description: `${total} total detections, ${draft} pending`,
      Icon: DataUsageIcon, 
      color: 'info.main', 
      trend: validated > 0 ? '+' + validated : '0' 
    },
    within: { 
      id: 'within', 
      title: "Within Response Time", 
      count: onTime, 
      progress: onTimeProgress, 
      description: `${onTimeProgress}% of total detections`,
      Icon: ScheduleIcon, 
      color: 'success.main', 
      trend: onTime > 0 ? '+' + onTime : '0'
    },
    temp: { 
      id: 'temp', 
      title: "Due Soon", 
      count: dueSoon, 
      progress: dueSoonProgress, 
      description: `${dueSoonProgress}% approaching deadline`,
      Icon: WarningAmberIcon, 
      color: 'warning.main', 
      trend: dueSoon > 0 ? '+' + dueSoon : '0'
    },
    perm: { 
      id: 'perm', 
      title: "Overdue", 
      count: overdue, 
      progress: overdueProgress, 
      description: `${overdueProgress}% past deadline`,
      Icon: ErrorIcon, 
      color: 'error.main', 
      trend: overdue > 0 ? '+' + overdue : '0'
    },
    closed: { 
      id: 'closed', 
      title: "Resolved", 
      count: resolved, 
      progress: resolvedProgress, 
      description: `${resolvedProgress}% of total detections`,
      Icon: CheckCircleIcon, 
      color: 'success.main', 
      trend: resolved > 0 ? '+' + resolved : '0'
    },
  };
};

// Helper function to generate random location decimals
const getRandomLocation = (baseSec) => {
  const randomDecimalPart = (Math.floor(Math.random() * 20) * 0.05).toFixed(2); 
  const integerPart = Math.floor(baseSec); 
  const finalValue = integerPart + parseFloat(randomDecimalPart);
  return `SEC${finalValue.toFixed(2)}`;
};

// Table data - cleared to show actual status (empty when no data)
const mockTableData = {
  total: [],
  within: [],
  temp: [],
  perm: [],
  closed: [],
};

// Function to get the appropriate status color palette object or specific grey color
const getStatusColorPalette = (status, theme) => {
  switch (status) {
    case 'Within Response Time': return theme.palette.success;
    case 'Temp Fix Overdue': return theme.palette.warning;
    case 'Perm Fix Overdue': return theme.palette.error;
    case 'Closed': return theme.palette.grey; 
    default: return theme.palette.text;
  }
};

// Function to determine if a trend is positive or negative
const getTrendDirection = (trend) => {
  if (!trend) return 'neutral';
  return trend.startsWith('+') ? 'up' : trend.startsWith('-') ? 'down' : 'neutral';
};

const DefectSummarySection = ({ user }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, getUserOrganization } = useAuth();
  
  // Get real defect statistics with permission filtering
  const { stats, loading: statsLoading, error: statsError, refreshStats } = useDefectStats(user);
  
  const [expandedCardId, setExpandedCardId] = useState(null); 
  // Modal state for detection details - DISABLED
  // const [isModalOpen, setIsModalOpen] = useState(false);
  // const [selectedDetectionData, setSelectedDetectionData] = useState(null);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  
  // Modal state for viewing defect details
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRowDefect, setSelectedRowDefect] = useState(null);
  
  // Table data state for expanded cards
  const [tableData, setTableData] = useState({});
  const [tableLoading, setTableLoading] = useState({});
  const [tableError, setTableError] = useState({});
  
  // Pagination state for each table
  const [tablePagination, setTablePagination] = useState({});
  const [tableTotalCounts, setTableTotalCounts] = useState({});

  // Generate defect data from real stats
  const defectData = getDefectDataFromStats(stats, statsLoading);
  
  // Get organization info
  const userOrganization = getUserOrganization();
  const organizationId = userOrganization?.id;

  // Function to fetch table data for a specific card
  const fetchTableDataForCard = useCallback(async (cardId, page = 0, rowsPerPage = 10) => {
    if (!organizationId) return;

    setTableLoading(prev => ({ ...prev, [cardId]: true }));
    setTableError(prev => ({ ...prev, [cardId]: null }));

    try {
      // Build filter parameters based on card type
      let params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort_by: 'first_detected_at',
        sort_order: 'DESC',
        boundary_status: 'in_boundary'
      };

      // 🔐 ADD PERMISSION-BASED FILTERING (same pattern as DefectsPage)
      if (user?.permissionScope === 'network_section') {
        const accessibleNetworkIds = user.accessible_networks || [];
        const accessibleSectionIds = user.accessible_sections || [];
        
        // Include both network and section filtering - backend will use OR logic
        if (accessibleNetworkIds.length > 0) {
          params.network_ids = accessibleNetworkIds.join(',');
        }
        
        if (accessibleSectionIds.length > 0) {
          params.section_ids = accessibleSectionIds.join(',');
        }
        
        // Validate user has any permissions at all
        const hasNetworkAccess = accessibleNetworkIds.length > 0;
        const hasSectionAccess = accessibleSectionIds.length > 0;
        
        if (!hasNetworkAccess && !hasSectionAccess) {
          console.warn(`[DefectSummarySection] User ${user.username} has no network or section permissions`);
          setTableData(prev => ({ ...prev, [cardId]: [] }));
          setTableTotalCounts(prev => ({ ...prev, [cardId]: 0 }));
          setTableLoading(prev => ({ ...prev, [cardId]: false }));
          return;
        }
        
        console.log(`🔒 [DefectSummarySection] Applying permission-based filtering for ${cardId}:`, {
          networkIds: params.network_ids,
          sectionIds: params.section_ids,
          userNetworks: accessibleNetworkIds.length,
          userSections: accessibleSectionIds.length
        });
      }

      // Add filters based on card type
      switch (cardId) {
        case 'total':
          // No additional filters - show all
          break;
        case 'within':
          // Show defects that are on time (using SLA logic)
          params.sla_status = 'on_time';
          break;
        case 'temp':
          // Show defects due soon
          params.sla_status = 'due_soon';
          break;
        case 'perm':
          // Show overdue defects
          params.sla_status = 'overdue';
          break;
        case 'closed':
          // Show resolved defects
          params.defect_status = 'resolved';
          break;
        case 'potholes':
          // Show only unresolved potholes - filter by defect type containing 'pothole' and not resolved
          // Remove specific API filters and filter client-side for now
          break;
        default:
          break;
      }

      const response = await envisFindingsApi.getTableFindings(params);
      let data = response.data.data || [];

      // Apply client-side filtering for potholes
      if (cardId === 'potholes') {
        // Ask server for exactly the same population as the card
        // Confirmed defects, unresolved, in-boundary, pothole only
        const potholeParams = {
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          sort_by: 'first_detected_at',
          sort_order: 'DESC',
          boundary_status: 'in_boundary',
          record_type: 'confirmed_defect',
          defect_statuses: 'new,in_progress',
          is_pothole: 'true'
        };

        // Apply permission filters
        if (user?.permissionScope === 'network_section') {
          const accessibleNetworkIds = user.accessible_networks || [];
          const accessibleSectionIds = user.accessible_sections || [];
          if (accessibleNetworkIds.length > 0) {
            potholeParams.network_ids = accessibleNetworkIds.join(',');
          }
          if (accessibleSectionIds.length > 0) {
            potholeParams.section_ids = accessibleSectionIds.join(',');
          }
        }

        const potholeResp = await envisFindingsApi.getTableFindings(potholeParams);
        data = potholeResp.data.data || [];
        const totalCount = potholeResp.data.total || 0;
        setTableTotalCounts(prev => ({ ...prev, [cardId]: totalCount }));
      } else {
        const totalCount = response.data.total || 0;
        setTableTotalCounts(prev => ({ ...prev, [cardId]: totalCount }));
      }

      // Transform data to match table structure
      const transformedData = data.map(item => {
        const deviceName = item.device_name || item.device_uuid;
        const deviceDisplay = item.vehicle_id 
          ? `${deviceName} (${item.vehicle_id})` 
          : deviceName;

        const defectTypeName = item.validated_defect_type_name || item.defect_type_name || 'Unknown';

        // Format location
        const networkLocation = [
          item.network_name,
          item.section_name,
          item.chainage_segment_name
        ].filter(Boolean).join(' • ');
        
        const isOutOfBoundary = !networkLocation;
        const coordinatesDisplay = `${item.latitude?.toFixed(4)}, ${item.longitude?.toFixed(4)}`;
        
        let locationDisplay;
        if (isOutOfBoundary) {
          locationDisplay = `🚫 Out of Boundary • ${coordinatesDisplay}`;
        } else {
          locationDisplay = networkLocation;
        }

        // Determine validation status based on record type
        let validationStatus = item.status;
        if (item.record_type === 'confirmed_defect') {
          validationStatus = 'validated'; // All confirmed defects are validated
        }

        // Format defect status - show only the status if defect exists
        let defectStatus = null;
        if (item.record_type === 'confirmed_defect' && item.defect_status) {
          defectStatus = item.defect_status;
        }

        return {
          id: item.id, // Use the raw finding ID for navigation
          displayId: item.record_type === 'confirmed_defect' ? `D-${item.defect_id}` : `F-${item.id}`,
          device: deviceDisplay,
          detection: defectTypeName,
          network: item.network_name || 'N/A',
          location: locationDisplay,
          bound: item.bound || 'N/A',
          detected: new Date(item.first_detected_at).toLocaleDateString(),
          validationStatus: validationStatus,
          defectStatus: defectStatus,
          hasWorkOrder: (item.work_order_count || 0) > 0,
          latestWorkOrderStatus: item.latest_work_order_status,
          rawData: item
        };
      });

      setTableData(prev => ({ ...prev, [cardId]: transformedData }));
    } catch (error) {
      console.error(`Failed to fetch table data for ${cardId}:`, error);
      setTableError(prev => ({ 
        ...prev, 
        [cardId]: `Failed to load ${cardId} data` 
      }));
    } finally {
      setTableLoading(prev => ({ ...prev, [cardId]: false }));
    }
  }, [organizationId, user]);

  const handleExpandClick = (cardId) => {
    const newExpandedId = expandedCardId === cardId ? null : cardId;
    setExpandedCardId(newExpandedId);
    
    // Initialize pagination state for new card
    if (newExpandedId && !tablePagination[cardId]) {
      setTablePagination(prev => ({
        ...prev,
        [cardId]: { page: 0, rowsPerPage: 10 }
      }));
    }
    
    // Fetch data when expanding a card
    if (newExpandedId && !tableData[cardId]) {
      const pagination = tablePagination[cardId] || { page: 0, rowsPerPage: 10 };
      fetchTableDataForCard(cardId, pagination.page, pagination.rowsPerPage);
    }
  };

  // Row click handler - DISABLED
  // const handleRowClick = (rowData) => {
  //   setSelectedDetectionData(rowData);
  //   setIsModalOpen(true);
  // };

  // const handleCloseModal = () => {
  //   setIsModalOpen(false);
  //   setSelectedDetectionData(null);
  // };

  const handleCardHover = (cardId) => {
    // Disable hover effects when any card is expanded to prevent table blinking
    if (!expandedCardId) {
      setHoveredCardId(cardId);
    }
  };

  // Pagination handlers
  const handleChangePage = (cardId, newPage) => {
    const currentPagination = tablePagination[cardId] || { page: 0, rowsPerPage: 10 };
    const updatedPagination = { ...currentPagination, page: newPage };
    
    setTablePagination(prev => ({
      ...prev,
      [cardId]: updatedPagination
    }));
    
    fetchTableDataForCard(cardId, newPage, currentPagination.rowsPerPage);
  };

  const handleChangeRowsPerPage = (cardId, newRowsPerPage) => {
    const updatedPagination = { page: 0, rowsPerPage: newRowsPerPage };
    
    setTablePagination(prev => ({
      ...prev,
      [cardId]: updatedPagination
    }));
    
    fetchTableDataForCard(cardId, 0, newRowsPerPage);
  };

  // Handle table row click to open view modal
  const handleRowClick = (rowData) => {
    // Transform the row data to match the format expected by DefectValidationModal
    const defectForModal = {
      id: rowData.displayId,
      recordType: rowData.rawData.record_type || 'pending_finding',
      defectId: rowData.rawData.defect_id,
      defectType: rowData.detection,
      confidence: rowData.rawData.confidence_score || 0.8,
      detectedAt: rowData.detected,
      location: rowData.location,
      rawData: rowData.rawData,
      originalBoundingBoxId: rowData.rawData.original_bounding_box_id,
      // Additional fields that might be needed
      validatedBy: rowData.rawData.validated_by_name,
      validatedAt: rowData.rawData.validated_at,
    };
    
    setSelectedRowDefect(defectForModal);
    setViewModalOpen(true);
  };

  // Handle modal close
  const handleViewModalClose = () => {
    setViewModalOpen(false);
    setSelectedRowDefect(null);
  };

  // Handle action icon click to navigate to the defects page
  const handleActionClick = (e, findingId) => {
    e.stopPropagation(); // Prevent the row's onClick from firing
    if (findingId) {
      navigate(`/defects?finding_id=${findingId}&openModal=true`);
    }
  };

  const cardStyle = {
    p: 0,
    borderRadius: 2,
    minHeight: '160px', // Consistent minimum height like AssetsPage
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 30px rgba(0, 0, 0, 0.08)',
    }
  };

  // Updated renderCard function
  const renderCard = (cardId, data) => {
    const { title, count, progress, description, Icon, color, trend, featured, slogan } = data;
    const colorParts = color.split('.');
    const cardColor = theme.palette[colorParts[0]]?.[colorParts[1] || 'main'] || colorParts[0]; 
    const isExpanded = expandedCardId === cardId;
    const isHovered = hoveredCardId === cardId;
    const trendDirection = getTrendDirection(trend);
    const isFeatured = featured === true;
    
    return (
      <Paper 
        sx={{
          ...cardStyle,
          ...(isFeatured && {
            background: `linear-gradient(135deg, ${alpha(cardColor, 0.15)} 0%, ${alpha(cardColor, 0.08)} 100%)`,
            border: `2px solid ${alpha(cardColor, 0.3)}`,
            transform: 'translateY(-2px)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 40px ${alpha(cardColor, 0.15)}`,
            }
          })
        }}
        onMouseEnter={() => handleCardHover(cardId)}
        onMouseLeave={() => handleCardHover(null)}
      >
          {/* Header section */}
          <Box 
            sx={{ 
              px: 2, 
              py: 1.5, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              borderColor: alpha(cardColor, 0.15),
              bgcolor: alpha(cardColor, 0.03)
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{ 
                  bgcolor: alpha(cardColor, 0.1), 
                  color: cardColor,
                  width: 32, 
                  height: 32,
                  mr: 1.5
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {title}
              </Typography>
            </Box>
            
            {trend && (
              <Chip 
                size="small"
                label={trend} 
                icon={trendDirection === 'up' ? <ArrowUpwardIcon fontSize="small" /> : 
                       trendDirection === 'down' ? <ArrowDownwardIcon fontSize="small" /> : null}
                sx={{ 
                  height: 20, 
                  fontSize: '0.7rem',
                  bgcolor: alpha(trendDirection === 'up' ? theme.palette.success.main : 
                                trendDirection === 'down' ? theme.palette.error.main : 
                                theme.palette.grey[500], 0.1),
                  color: trendDirection === 'up' ? theme.palette.success.dark : 
                         trendDirection === 'down' ? theme.palette.error.dark : 
                         theme.palette.grey[700],
                  fontWeight: 600,
                  '& .MuiChip-icon': { fontSize: '0.7rem' }
                }}
              />
            )}
          </Box>
          
          {/* Count section */}
          <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography 
              variant="h2" 
              component="div" 
              sx={{ 
                fontWeight: 800, 
                mb: 0.5,
                backgroundImage: `linear-gradient(135deg, ${cardColor}, ${alpha(cardColor, 0.7)})`,
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {count.toLocaleString()}
            </Typography>
            
            <Typography 
              variant="caption" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontWeight: 500,
                fontSize: '0.75rem'
              }}
            >
              {description}
            </Typography>
            
            {/* Featured slogan */}
            {isFeatured && slogan && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  mt: 0.5,
                  display: 'block',
                  fontStyle: 'italic'
                }}
              >
                🎯 {slogan}
              </Typography>
            )}
          </Box>

          {/* Progress bar section */}
          <Box sx={{ px: 2, pb: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                bgcolor: alpha(cardColor, 0.15), 
                '& .MuiLinearProgress-bar': {
                  bgcolor: cardColor,
                  borderRadius: 3,
                }
              }} 
            />
          </Box>
          
          {/* Expand Button */}
          <Fade in={isHovered || isExpanded}>
            <IconButton
              onClick={() => handleExpandClick(cardId)}
              sx={{
                position: 'absolute',
                bottom: 6,
                right: 6,
                bgcolor: alpha(theme.palette.background.paper, 0.9),
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shortest,
                }),
                '&:hover': {
                  bgcolor: theme.palette.background.paper,
                }
              }}
              size="small"
              aria-expanded={isExpanded}
              aria-label="show more"
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Fade>
        </Paper>
    );
  };

  // Updated Detail Table Component with Real Data
  const DetailTable = ({ cardId }) => {
    const data = tableData[cardId] || [];
    const loading = tableLoading[cardId] || false;
    const error = tableError[cardId] || null;
    const title = defectData[cardId]?.title || 'Details';
    const count = defectData[cardId]?.count || 0;
    const cardColor = defectData[cardId]?.color || 'grey.600';
    const colorParts = cardColor.split('.');
    const mainColor = theme.palette[colorParts[0]]?.[colorParts[1] || 'main'] || colorParts[0];
    
    // Get pagination state for this card
    const pagination = tablePagination[cardId] || { page: 0, rowsPerPage: 10 };
    const totalCount = tableTotalCounts[cardId] || 0;

    // Show loading state
    if (loading) {
      return (
        <Fade in={true} timeout={400}>
          <Paper sx={{ 
            mt: 2, 
            borderRadius: 2, 
            overflow: 'hidden', 
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
            border: `1px solid ${alpha(mainColor, 0.2)}`,
            p: 4,
            textAlign: 'center'
          }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Loading {title.toLowerCase()} data...
            </Typography>
          </Paper>
        </Fade>
      );
    }

    // Show error state
    if (error) {
      return (
        <Fade in={true} timeout={400}>
          <Paper sx={{ 
            mt: 2, 
            borderRadius: 2, 
            overflow: 'hidden', 
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            p: 4,
            textAlign: 'center'
          }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Try again
            </Typography>
          </Paper>
        </Fade>
      );
    }

    return (
      <Fade in={true} timeout={400}>
        <Paper sx={{ 
          mt: 2, 
          borderRadius: 2, 
          overflow: 'hidden', 
          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
          border: `1px solid ${alpha(mainColor, 0.2)}`
        }}>
          <Box sx={{ 
            p: 2, 
            bgcolor: alpha(mainColor, 0.05),
            borderBottom: `1px solid ${alpha(mainColor, 0.2)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Badge 
                badgeContent={count} 
                color={colorParts[0] !== 'grey' ? colorParts[0] : 'default'}
                sx={{ '& .MuiBadge-badge': { fontWeight: 600 } }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
              </Badge>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Chip 
                icon={<FilterListIcon />} 
                label={`${totalCount} items`} 
                size="small" 
                variant="outlined" 
                sx={{ borderRadius: 1.5 }}
              />
            </Stack>
          </Box>
          <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Device</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Defect Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Detected</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Validation Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Defect Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.map((row, index) => {
                    const getValidationStatusChip = (status) => {
                      const statusConfig = {
                        validated: { label: 'Validated', color: 'success' },
                        rejected: { label: 'Rejected', color: 'error' },
                        pending: { label: 'Pending', color: 'info' },
                        draft: { label: 'Draft', color: 'default' },
                      };
                      const config = statusConfig[status] || { label: status, color: 'default' };
                      return (
                        <Chip
                          size="small"
                          label={config.label}
                          color={config.color}
                          sx={{ fontWeight: 600, borderRadius: 1, fontSize: '0.7rem', height: 22 }}
                        />
                      );
                    };

                    const getDefectStatusChip = (status, rowData) => {
                      if (rowData.hasWorkOrder && rowData.latestWorkOrderStatus) {
                        const workOrderStatusConfig = {
                          pending: { label: 'WO: Pending', color: 'default' },
                          assigned: { label: 'WO: Assigned', color: 'info' },
                          in_progress: { label: 'WO: In Progress', color: 'warning' },
                          completed: { label: 'WO: Completed', color: 'success' },
                          cancelled: { label: 'WO: Cancelled', color: 'error' },
                        };
                        const config = workOrderStatusConfig[rowData.latestWorkOrderStatus] || { label: `WO: ${rowData.latestWorkOrderStatus}`, color: 'default' };
                        return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
                      }

                      if (!status) {
                        return <Typography variant="body2" color="text.secondary">-</Typography>;
                      }

                      const statusConfig = {
                        new: { label: 'New', color: 'info' },
                        in_progress: { label: 'In Progress', color: 'warning' },
                        resolved: { label: 'Resolved', color: 'success' },
                      };
                      const config = statusConfig[status] || { label: status, color: 'default' };
                      return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
                    };

                  return (
                    <TableRow
                        key={`${row.id}-${index}`}
                      hover
                        onClick={() => handleRowClick(row)}
                        sx={{
                          '&:hover td': { bgcolor: alpha(mainColor, 0.02) },
                          '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.grey[50], 0.5) },
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: alpha(mainColor, 0.08),
                            cursor: 'pointer',
                          }
                        }}
                    >
                        <TableCell sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                          {row.displayId}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 150 }}>
                          <Typography variant="caption" sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {row.device}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {row.detection}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="caption" sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: row.location.includes('🚫') ? 'warning.main' : 'text.secondary'
                          }}>
                            {row.location}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {row.detected}
                          </Typography>
                        </TableCell>
                      <TableCell>
                        {getValidationStatusChip(row.validationStatus)}
                      </TableCell>
                      <TableCell>
                        {getDefectStatusChip(row.defectStatus, row)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View & Action" placement="top">
                          <IconButton
                            size="small"
                            onClick={(e) => handleActionClick(e, row.id)}
                            sx={{
                              '&:hover': {
                                color: 'primary.main'
                              }
                            }}
                          >
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No {title.toLowerCase()} data found
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Try refreshing or check if there are any {title.toLowerCase()} in your organization
                      </Typography>
                      </TableCell>
                    </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Sticky Pagination at bottom */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={(event, newPage) => handleChangePage(cardId, newPage)}
            onRowsPerPageChange={(event) => handleChangeRowsPerPage(cardId, parseInt(event.target.value, 10))}
            showFirstButton
            showLastButton
            sx={{
              position: 'sticky',
              bottom: 0,
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              zIndex: 1,
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)'
            }}
          />
        </Paper>
      </Fade>
    );
  };

  // Don't render if not authenticated or no organization
  if (!isAuthenticated || !organizationId) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {!isAuthenticated ? 'Authentication required' : 'No organization access'}
        </Typography>
      </Box>
    );
  }

  // Show error state
  if (statsError) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="error">
          {statsError}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Unable to load defect statistics
        </Typography>
      </Box>
    );
  }

  return (
    <Box> 
      {/* Header with refresh info */}
      {!statsLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Real-time data from ENViS devices • Last updated: {new Date().toLocaleTimeString()}
          </Typography>
        </Box>
      )}

      <Grid 
        container 
        spacing={2}
        sx={{
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(3, 1fr)', 
            lg: 'repeat(4, 1fr)',
            xl: 'repeat(6, 1fr)' 
          },
          gap: 2,
          '& > .MuiGrid-item': {
            padding: 0, // Remove default Grid item padding since we're using gap
          }
        }}
      > 
        {Object.entries(defectData).map(([key, data]) => (
          <Box key={key}>
            {renderCard(key, data)}
          </Box>
        ))}
      </Grid>
      
      <Collapse in={Boolean(expandedCardId)} timeout="auto" unmountOnExit>
        {expandedCardId && <DetailTable cardId={expandedCardId} />}
      </Collapse>

      {/* Modal - DISABLED */}
      {/* <DetectionDetailModal 
        open={isModalOpen}
        onClose={handleCloseModal}
        detection={selectedDetectionData}
      /> */}

      {/* View-only modal for defect details */}
      {selectedRowDefect && (
        <DefectValidationModal
          open={viewModalOpen}
          onClose={handleViewModalClose}
          selectedDefect={selectedRowDefect}
          defectTypesList={[]} // Empty array for dashboard, will be fetched by modal if needed
          currentDefectTypeId={selectedRowDefect.rawData?.defect_type_id || selectedRowDefect.rawData?.validated_defect_type_id}
          onDefectTypeChange={() => {}} // No-op for view-only
          rejectionReason=""
          onRejectionReasonChange={() => {}} // No-op for view-only
          loadingDefectTypes={false}
          onValidate={() => {}} // No-op for view-only
          onReject={() => {}} // No-op for view-only
          formatDateTime={(date) => {
            if (!date) return 'N/A';
            return new Date(date).toLocaleString();
          }}
          viewOnly={true} // This makes the modal read-only
        />
      )}
    </Box>
  );
};

export default DefectSummarySection;